/**
 * Streaming Run Store — Manages real-time SSE monitoring for draft workflow runs.
 *
 * Architecture:
 * - Owns StreamingRunState (internal shape from SSE events)
 * - Projects into editorRunStore via toEditorRunState() bridge
 * - RAF batching prevents render amplification (many SSE events → 1 render)
 * - One-way fallback to polling on any error (never reconnect — POST creates a new run)
 *
 * Lifecycle:
 *   startStream() → POST response → enter execution mode → SSE events → RAF batch
 *   → project to editorRunStore. Error or RunCompleted → cleanup.
 *   Returns Promise<boolean> so caller can detect POST failures before closing dialog.
 */

import { create } from 'zustand';
import { streamDraftRun } from '@/services/runStreamClient';
import type { RunStreamCallbacks } from '@/services/runStreamClient';
import type { MonitoringFetchOptions } from '@/services/monitoringApi';
import {
  createStreamingRunState,
  applyMonitoringEvent,
  toEditorRunState,
} from '@/types/streamingMonitoring';
import type {
  RunMonitoringEvent,
  StreamingRunState,
  RunStreamError,
} from '@/types/streamingMonitoring';
import { useEditorRunStore } from './editorRunStore';
import { telemetryService } from '@/services/telemetryService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StreamingStatus =
  | 'idle'           // No active stream
  | 'connecting'     // POST sent, waiting for response
  | 'streaming'      // Receiving events
  | 'completed'      // RunCompleted received, stream ended normally
  | 'error'          // Error occurred, stream dead
  | 'aborted';       // User navigated away or cancelled

export interface StreamingRunStoreState {
  /** Current streaming lifecycle status */
  status: StreamingStatus;
  /** Internal streaming state (null until RunSnapshot received) */
  streamState: StreamingRunState | null;
  /** Last error, if any */
  lastError: RunStreamError | null;
  /** Total events received in current stream */
  eventCount: number;
  /** Run ID once known (from RunSnapshot) */
  runId: string | null;
  /** Workflow name of the current stream */
  workflowName: string | null;
}

interface StreamingRunStoreActions {
  /**
   * Start streaming a draft run.
   * Returns true if the HTTP POST succeeded (stream is now active).
   * Returns false if the POST failed or streaming is not supported.
   * Caller should only close dialog / enter execution mode on true.
   */
  startStream: (
    fetchOptions: MonitoringFetchOptions,
    triggerName: string,
    streamCallbackUrl: string,
    body?: unknown,
  ) => Promise<boolean>;

  /**
   * Stop the current stream. Safe to call if not streaming.
   */
  stopStream: () => void;

  /**
   * Reset all state (e.g., when navigating to a different workflow).
   */
  reset: () => void;
}

type StreamingRunStore = StreamingRunStoreState & StreamingRunStoreActions;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Silence timeout — if no events arrive within this period, assume stream is dead */
const SILENCE_TIMEOUT_MS = 30_000;

/** Absolute timeout — maximum stream duration before forcing fallback */
const ABSOLUTE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

const INITIAL_STATE: StreamingRunStoreState = {
  status: 'idle',
  streamState: null,
  lastError: null,
  eventCount: 0,
  runId: null,
  workflowName: null,
};

export const useStreamingRunStore = create<StreamingRunStore>()((set, get) => {
  // Module-scoped mutable refs — wrapped in closure for this store instance.
  // Using a container object so refs survive across store recreations.
  const refs = {
    abortController: null as AbortController | null,
    silenceTimerId: null as ReturnType<typeof setTimeout> | null,
    absoluteTimerId: null as ReturnType<typeof setTimeout> | null,
    rafHandle: null as number | null,
    projectionDirty: false,
    /** Whether the store is still mounted (for post-unmount guard) */
    active: true,
    /** Resolve the postResultPromise from startStream — ensures it never hangs */
    resolvePostResult: null as ((ok: boolean) => void) | null,
  };

  // -------------------------------------------------------------------
  // Internal helpers
  // -------------------------------------------------------------------

  /** Reset silence timer (called on every event + on first SSE event) */
  function resetSilenceTimer() {
    if (refs.silenceTimerId !== null) clearTimeout(refs.silenceTimerId);
    refs.silenceTimerId = setTimeout(() => {
      const { status } = get();
      if (status === 'streaming') {
        telemetryService.trackEvent('sse.runstream.silence_timeout', {
          workflowName: get().workflowName ?? 'unknown',
        }, { timeout_ms: SILENCE_TIMEOUT_MS });

        handleStreamError({
          type: 'timeout',
          message: `No events received for ${SILENCE_TIMEOUT_MS / 1000}s`,
          runId: get().runId ?? undefined,
        });
      }
    }, SILENCE_TIMEOUT_MS);
  }

  /** Clear all timers and RAF */
  function clearTimers() {
    if (refs.silenceTimerId !== null) { clearTimeout(refs.silenceTimerId); refs.silenceTimerId = null; }
    if (refs.absoluteTimerId !== null) { clearTimeout(refs.absoluteTimerId); refs.absoluteTimerId = null; }
    if (refs.rafHandle !== null) { cancelAnimationFrame(refs.rafHandle); refs.rafHandle = null; }
    refs.projectionDirty = false;
  }

  /**
   * Schedule a RAF-batched projection of streamState → editorRunStore.
   * Multiple events arriving in the same frame produce a single re-render.
   */
  function scheduleProjection() {
    refs.projectionDirty = true;
    if (refs.rafHandle !== null) return; // already scheduled

    refs.rafHandle = requestAnimationFrame(() => {
      refs.rafHandle = null;
      if (!refs.projectionDirty || !refs.active) return;
      refs.projectionDirty = false;

      const { streamState } = get();
      if (!streamState) return;

      // workflowId is the ARM resource ID — unavailable during streaming.
      // Downstream code uses workflowName (set from streaming.flowName) instead.
      const editorState = toEditorRunState(streamState, '');
      useEditorRunStore.getState().mergeStreamingExecution(editorState);
    });
  }

  /** Handle a single parsed SSE event */
  function handleEvent(event: RunMonitoringEvent) {
    const { status, streamState, eventCount } = get();
    if (status !== 'connecting' && status !== 'streaming') return; // stale event

    let newState: StreamingRunState;
    let newStatus: StreamingStatus = 'streaming';

    if (!streamState) {
      // First event — should be RunSnapshot but we handle any event
      if (event.kind !== 'runSnapshot') {
        telemetryService.trackEvent('sse.runstream.unexpected_first_event', {
          kind: event.kind,
          workflowName: get().workflowName ?? 'unknown',
        });
      }
      try {
        newState = createStreamingRunState(event as import('@/types/streamingMonitoring').RunSnapshotEvent);
        // If the first event wasn't a RunSnapshot, also apply it so action
        // data (actionName, status, etc.) isn't silently dropped.
        if (event.kind !== 'runSnapshot') {
          newState = applyMonitoringEvent(newState, event);
        }
      } catch (err) {
        handleStreamError({
          type: 'stream-error',
          message: err instanceof Error ? err.message : 'Failed to initialize streaming state',
        });
        return;
      }

      // Start silence timer on FIRST event (not on connect — avoids false
      // timeout while backend is starting the run)
      resetSilenceTimer();
    } else {
      newState = applyMonitoringEvent(streamState, event);
      // Reset silence timer on subsequent events
      resetSilenceTimer();
    }

    // Check if run completed
    if (event.kind === 'runCompleted') {
      newStatus = 'completed';
    }

    set({
      status: newStatus,
      streamState: newState,
      eventCount: eventCount + 1,
      runId: newState.runId,
    });

    // Schedule batched projection to editorRunStore
    scheduleProjection();

    // If completed, clean up
    if (newStatus === 'completed') {
      clearTimers();
      // Final synchronous projection to ensure last state is applied
      const editorState = toEditorRunState(newState, '');
      useEditorRunStore.getState().mergeStreamingExecution(editorState);

      telemetryService.trackEvent('sse.runstream.run_completed', {
        workflowName: get().workflowName ?? 'unknown',
        runStatus: newState.status,
      }, { event_count: eventCount + 1 });
    }
  }

  /**
   * Handle stream error — sets error state, cleans up.
   * For pre-connection errors ('not-supported', 'connection-failed'),
   * clears execution mode so user isn't stuck.
   */
  function handleStreamError(error: RunStreamError) {
    clearTimers();
    refs.abortController?.abort();
    refs.abortController = null;

    // Ensure postResultPromise never hangs — resolve it on error
    if (refs.resolvePostResult) {
      refs.resolvePostResult(false);
      refs.resolvePostResult = null;
    }

    const hadStreamState = get().streamState !== null;

    set({
      status: 'error',
      lastError: error,
    });

    telemetryService.trackEvent('sse.runstream.failed', {
      type: error.type,
      workflowName: get().workflowName ?? 'unknown',
    });

    // If error occurred before any events were received (no streamState),
    // exit execution mode — user would be stuck with a phantom placeholder.
    // For mid-flight errors (streamState exists), keep execution mode so
    // user can see partial results + fallback to polling.
    if (!hadStreamState) {
      useEditorRunStore.getState().clearExecution();
    }
  }

  /** Handle stream completion (EOF without RunCompleted) */
  function handleStreamComplete() {
    const { status, streamState } = get();
    // Guard: don't overwrite terminal states (completed, error, OR aborted)
    if (status === 'completed' || status === 'error' || status === 'aborted') return;

    clearTimers();
    refs.abortController = null;

    // If we have state but no RunCompleted, mark as completed anyway
    if (streamState) {
      set({ status: 'completed' });

      // Final projection — use merge to preserve pinData/agentIterations
      const editorState = toEditorRunState(streamState, '');
      useEditorRunStore.getState().mergeStreamingExecution(editorState);
    } else {
      // Stream ended before any events — treat as error
      set({
        status: 'error',
        lastError: {
          type: 'stream-error',
          message: 'Stream ended without any events',
        },
      });
      useEditorRunStore.getState().clearExecution();
    }
  }

  // -------------------------------------------------------------------
  // Public actions
  // -------------------------------------------------------------------

  return {
    ...INITIAL_STATE,

    startStream: async (fetchOptions, triggerName, streamCallbackUrl, body) => {
      // Clean up any existing stream WITHOUT setting 'aborted' status
      // (avoids a transient flash between aborted→connecting)
      const { status: prevStatus } = get();
      if (prevStatus === 'connecting' || prevStatus === 'streaming') {
        clearTimers();
        if (refs.abortController) {
          refs.abortController.abort();
          refs.abortController = null;
        }
      }

      // Enter connecting state
      set({
        ...INITIAL_STATE,
        status: 'connecting',
        workflowName: fetchOptions.workflowName,
      });

      // Create abort controller
      const abortController = new AbortController();
      refs.abortController = abortController;

      // Build callbacks
      const callbacks: RunStreamCallbacks = {
        onEvent: handleEvent,
        onComplete: handleStreamComplete,
        onError: handleStreamError,
      };

      // Await the POST response. streamDraftRun resolves the Promise
      // when the stream ends (success, error, or abort). But more importantly,
      // it calls onError synchronously within the same tick if the POST fails
      // (4xx, network error). So we need a different approach: wrap in a
      // Promise that resolves as soon as we know the HTTP response succeeded.
      let resolvePostResult: (ok: boolean) => void;
      const postResultPromise = new Promise<boolean>((resolve) => {
        resolvePostResult = resolve;
      });
      // Store resolver in refs so handleStreamError/stopStream can resolve it
      refs.resolvePostResult = resolvePostResult!;

      // Start the stream — fire and forget the stream reading.
      // We detect POST success/failure via the callbacks.
      let firstCallbackFired = false;

      const wrappedCallbacks: RunStreamCallbacks = {
        onPostSuccess: (httpRunId) => {
          if (!firstCallbackFired) {
            firstCallbackFired = true;

            // Store the run ID from HTTP response headers immediately — this is
            // the earliest reliable source, available before any SSE events arrive.
            if (httpRunId) {
              set({ runId: httpRunId });
            }

            // Enter execution mode with a placeholder (use real runId if available)
            const placeholderState = {
              runId: httpRunId ?? 'pending',
              executionId: 'pending',
              workflowId: '', // Real ARM ID arrives via RunSnapshot event
              workflowName: fetchOptions.workflowName,
              runStatus: 'running' as const,
              runStartTime: new Date().toISOString(),
              loadedAt: new Date().toISOString(),
              nodeRunData: {},
              pinData: {},
            };
            useEditorRunStore.getState().loadExecution(placeholderState);
            resolvePostResult!(true);
          }
        },
        onEvent: (event) => {
          if (!firstCallbackFired) {
            // onPostSuccess was not called (e.g., older backend without the header).
            // Fall back to the original first-event resolution path.
            firstCallbackFired = true;
            resolvePostResult!(true);

            // Enter execution mode with a placeholder on first event
            const placeholderState = {
              runId: 'pending',
              executionId: 'pending',
              workflowId: '', // Real ARM ID arrives via RunSnapshot event
              workflowName: fetchOptions.workflowName,
              runStatus: 'running' as const,
              runStartTime: new Date().toISOString(),
              loadedAt: new Date().toISOString(),
              nodeRunData: {},
              pinData: {},
            };
            useEditorRunStore.getState().loadExecution(placeholderState);
          }
          callbacks.onEvent(event);
        },
        onComplete: () => {
          if (!firstCallbackFired) {
            firstCallbackFired = true;
            // Stream closed before any events — still "succeeded" POST-wise
            // but handleStreamComplete will set error state
            resolvePostResult!(true);
          }
          callbacks.onComplete();
        },
        onError: (error) => {
          if (!firstCallbackFired) {
            firstCallbackFired = true;
            resolvePostResult!(false);
          }
          callbacks.onError(error);
        },
      };

      // Start absolute timeout
      refs.absoluteTimerId = setTimeout(() => {
        const currentStatus = get().status;
        if (currentStatus === 'connecting' || currentStatus === 'streaming') {
          telemetryService.trackEvent('sse.runstream.absolute_timeout', {
            workflowName: fetchOptions.workflowName,
          }, { timeout_ms: ABSOLUTE_TIMEOUT_MS });

          handleStreamError({
            type: 'timeout',
            message: `Stream exceeded maximum duration of ${ABSOLUTE_TIMEOUT_MS / 60000} minutes`,
            runId: get().runId ?? undefined,
          });
        }
      }, ABSOLUTE_TIMEOUT_MS);

      // NOTE: Do NOT start silence timer here — wait for first event.
      // Starting it here would cause false timeouts during cold starts.

      // Fire the stream — don't await (it resolves when stream ends)
      streamDraftRun({
        fetchOptions,
        triggerName,
        streamCallbackUrl,
        body,
        signal: abortController.signal,
        callbacks: wrappedCallbacks,
      }).catch((err) => {
        // Catch unexpected errors — should not normally happen
        if (!firstCallbackFired) {
          firstCallbackFired = true;
          resolvePostResult!(false);
        }
        telemetryService.trackException(err instanceof Error ? err : new Error(String(err)), {
          context: 'streamDraftRun.unexpected',
          workflowName: fetchOptions.workflowName,
        });
      });

      // Wait for the POST to succeed or fail
      return postResultPromise;
    },

    stopStream: () => {
      clearTimers();

      if (refs.abortController) {
        refs.abortController.abort();
        refs.abortController = null;
      }

      // Ensure postResultPromise never hangs — resolve it on abort
      if (refs.resolvePostResult) {
        refs.resolvePostResult(false);
        refs.resolvePostResult = null;
      }

      const wasStreaming = get().status === 'streaming' || get().status === 'connecting';
      if (wasStreaming) {
        set({ status: 'aborted' });
      }
    },

    reset: () => {
      get().stopStream();
      set(INITIAL_STATE);
    },
  };
});
